import styled from 'styled-components';
import React, { useMemo } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import QuestionDistributionChart from './QuestionDistributionChart';
import MiniQuestionChart from './MiniQuestionChart';
import Text from 'new_components/atoms/Text';
import colors from 'styles/colors';
import typography from 'styles/typography';

const chartOptions = [
    { name: "Low", color: "#F15062" },
    { name: "Neutral", color: "#B3B3B3" },
    { name: "High", color: "#10B981" },
];

const QuestionContent = ({
    question,
    core_data,
    data,
    selectedQuestion,
    navigation,
    rule,
    isFactor = false,
    customRanges = {
        Low: { min: 0, max: 4.99 },
        Neutral: { min: 5, max: 6.99 },
        High: { min: 7, max: 10 }
    },
    sortDirection = 'desc',
    calculationMethod = 'individual',
    minCompletionThreshold = 50
}) => {
    const anchor = useSelector((state) => state.audit?.anchor, shallowEqual);

    // Check if this is a factor or a single question
    const isFactorView = isFactor || selectedQuestion.questionCount !== undefined;

    const { distributionData, anchorBreakdowns } = useMemo(() => {
        if (!data || !selectedQuestion) return { distributionData: null, anchorBreakdowns: null };



        // Get all responses for the selected question/factor with proper filtering
        let responses;
        let totalPotentialResponses = 0;
        let skippedResponses = 0;
        
        if (isFactorView) {
            // Handle factor aggregation based on calculation method
            const scores = [];
            
            if (selectedQuestion.isOutcome) {
                const outcomeCategory = core_data.outcomeQuestions?.questions?.[selectedQuestion.outcomeCategory];
                if (!outcomeCategory) return { distributionData: null, anchorBreakdowns: null };
                
                if (calculationMethod === 'individual') {
                    // Pool ALL question responses (no per-person averaging)
                    data.forEach(response => {
                        if (!response.employee_outcomes || !response.employee_outcomes.responses) {
                            return;
                        }
                        
                        const validResponses = response.employee_outcomes.responses
                            .filter(r => outcomeCategory.questions.some(q => q.q === r.q && q.s === r.s))
                            .map(r => r.response)
                            .filter(val => val !== null && val !== undefined && val !== 'N/A' && val !== '' && !isNaN(Number(val)))
                            .map(val => Number(val));
                        
                        totalPotentialResponses += validResponses.length;
                        scores.push(...validResponses);
                    });
                } else {
                    // Calculate per-person averages
                    data.forEach(response => {
                        totalPotentialResponses++;
                        
                        if (!response.employee_outcomes || !response.employee_outcomes.responses) {
                            skippedResponses++;
                            return;
                        }
                        
                        const validPersonResponses = response.employee_outcomes.responses
                            .filter(r => outcomeCategory.questions.some(q => q.q === r.q && q.s === r.s))
                            .map(r => r.response)
                            .filter(val => val !== null && val !== undefined && val !== 'N/A' && val !== '' && !isNaN(Number(val)))
                            .map(val => Number(val));
                        
                        const totalQuestions = outcomeCategory.questions.length;
                        const minRequired = Math.ceil(totalQuestions * (minCompletionThreshold / 100));
                        if (validPersonResponses.length >= minRequired) {
                            const average = validPersonResponses.reduce((sum, val) => sum + val, 0) / validPersonResponses.length;
                            scores.push(average);
                        } else {
                            skippedResponses++;
                        }
                    });
                }
            } else {
                const dimension = core_data.questions?.dimensions?.[selectedQuestion.dimension];
                const factor = dimension?.factors?.[selectedQuestion.factor];
                if (!factor) return { distributionData: null, anchorBreakdowns: null };
                
                if (calculationMethod === 'individual') {
                    // Pool ALL question responses (no per-person averaging)
                    data.forEach(response => {
                        const validResponses = (response.questions || [])
                            .filter(q => q.id === selectedQuestion.dimension && q.factor === selectedQuestion.factor)
                            .map(q => q.response)
                            .filter(val => val !== null && val !== undefined && val !== 'N/A' && val !== '' && !isNaN(Number(val)))
                            .map(val => Number(val));
                        
                        totalPotentialResponses += validResponses.length;
                        scores.push(...validResponses);
                    });
                } else {
                    // Calculate per-person averages
                    data.forEach(response => {
                        totalPotentialResponses++;
                        
                        const validPersonResponses = (response.questions || [])
                            .filter(q => q.id === selectedQuestion.dimension && q.factor === selectedQuestion.factor)
                            .map(q => q.response)
                            .filter(val => val !== null && val !== undefined && val !== 'N/A' && val !== '' && !isNaN(Number(val)))
                            .map(val => Number(val));
                        
                        const totalQuestions = factor.questions.length;
                        const minRequired = Math.ceil(totalQuestions * (minCompletionThreshold / 100));
                        if (validPersonResponses.length >= minRequired) {
                            const average = validPersonResponses.reduce((sum, val) => sum + val, 0) / validPersonResponses.length;
                            scores.push(average);
                        } else {
                            skippedResponses++;
                        }
                    });
                }
            }
            
            responses = scores;
        } else if (selectedQuestion.isOutcome) {
            // Handle single outcome questions
            const allResponses = data.flatMap(response => {
                if (!response.employee_outcomes || !response.employee_outcomes.responses) {
                    return [];
                }
                return response.employee_outcomes.responses.filter(r => 
                    r.q === selectedQuestion.q && r.s === selectedQuestion.s
                ).map(r => r.response);
            });
            
            totalPotentialResponses = allResponses.length;
            skippedResponses = allResponses.filter(val => 
                val === null || val === undefined || val === 'N/A' || val === '' || isNaN(Number(val))
            ).length;
            
            responses = allResponses;
        } else {
            // Handle single regular questions
            const allResponses = data.map(response => {
                // Find all questions that match our selected question's identifiers
                const matchingQuestions = response.questions?.filter(q => 
                    q.id === selectedQuestion.dimension && 
                    q.factor === selectedQuestion.factor && 
                    q.q === selectedQuestion.q
                );
                // Get all responses from matching questions
                return matchingQuestions?.map(q => q.response);
            }).flat();
            
            totalPotentialResponses = allResponses.length;
            skippedResponses = allResponses.filter(val => 
                val === null || val === undefined || val === 'N/A' || val === '' || isNaN(Number(val))
            ).length;
            
            responses = allResponses;
        }
        
        // Filter and convert responses (apply to all response extraction paths)
        responses = responses.filter(val => {
            // Filter out null, undefined, 'N/A' responses and ensure it's a valid number
            return val !== null && 
                   val !== undefined && 
                   val !== 'N/A' && 
                   val !== '' &&
                   !isNaN(Number(val));
        }).map(val => Number(val)); // Convert to numbers to handle both int and float responses

        if (responses.length === 0) return { distributionData: null, anchorBreakdowns: null };
        
        // Check minimum response threshold based on Whitelabel rule
        const minimumResponses = core_data?.Whitelabel?.rule || 2;
        if (responses.length <= minimumResponses) return { distributionData: null, anchorBreakdowns: null };

        // Count responses in each category using exclusive upper bounds to avoid gaps
        const lowCount = responses.filter(score => score >= 0 && score < customRanges.Neutral.min).length;
        const neutralCount = responses.filter(score => score >= customRanges.Neutral.min && score < customRanges.High.min).length;
        const highCount = responses.filter(score => score >= customRanges.High.min && score <= 10).length;
        
        // Total valid responses that fall within our expected ranges
        const totalCategorizedResponses = lowCount + neutralCount + highCount;
        
        if (totalCategorizedResponses === 0) return { distributionData: null, anchorBreakdowns: null };

        // Calculate distribution with guaranteed 100% total
        const distribution = [
            {
                name: "Low",
                data: [(lowCount / totalCategorizedResponses) * 100],
                color: chartOptions[0].color,
                count: lowCount
            },
            {
                name: "Neutral", 
                data: [(neutralCount / totalCategorizedResponses) * 100],
                color: chartOptions[1].color,
                count: neutralCount
            },
            {
                name: "High",
                data: [(highCount / totalCategorizedResponses) * 100], 
                color: chartOptions[2].color,
                count: highCount
            }
        ];

        // Calculate anchor breakdowns
        let anchorBreakdowns = null;
        if (core_data?.categories?.[0]?.categories) {
            const categories = core_data.categories[0].categories;
            const anchorCategory = categories.find(cat => cat.id === anchor);

            if (anchorCategory) {
                anchorBreakdowns = anchorCategory.options.map(option => {
                    // Filter responses for this category option with proper filtering
                    const categoryResponses = data
                        .filter(response => {
                            const categoryResponse = response.categories?.find(cat => cat.id === anchor);
                            return categoryResponse?.response === option.id;
                        })
                        .map(response => {
                            if (isFactorView) {
                                // Handle factor aggregation for anchor breakdowns based on calculation method
                                if (selectedQuestion.isOutcome) {
                                    const outcomeCategory = core_data.outcomeQuestions?.questions?.[selectedQuestion.outcomeCategory];
                                    if (!outcomeCategory) return [];
                                    
                                    if (!response.employee_outcomes || !response.employee_outcomes.responses) {
                                        return [];
                                    }
                                    
                                    const personResponses = response.employee_outcomes.responses
                                        .filter(r => outcomeCategory.questions.some(q => q.q === r.q && q.s === r.s))
                                        .map(r => r.response)
                                        .filter(val => val !== null && val !== undefined && val !== 'N/A' && val !== '' && !isNaN(Number(val)))
                                        .map(val => Number(val));
                                    
                                    if (calculationMethod === 'individual') {
                                        // Return all individual responses
                                        return personResponses;
                                    } else {
                                        // Calculate per-person average
                                        const totalQuestions = outcomeCategory.questions.length;
                                        const minRequired = Math.ceil(totalQuestions * (minCompletionThreshold / 100));
                                        if (personResponses.length >= minRequired) {
                                            const average = personResponses.reduce((sum, val) => sum + val, 0) / personResponses.length;
                                            return [average];
                                        }
                                        return [];
                                    }
                                } else {
                                    const dimension = core_data.questions?.dimensions?.[selectedQuestion.dimension];
                                    const factor = dimension?.factors?.[selectedQuestion.factor];
                                    if (!factor) return [];
                                    
                                    const personResponses = (response.questions || [])
                                        .filter(q => q.id === selectedQuestion.dimension && q.factor === selectedQuestion.factor)
                                        .map(q => q.response)
                                        .filter(val => val !== null && val !== undefined && val !== 'N/A' && val !== '' && !isNaN(Number(val)))
                                        .map(val => Number(val));
                                    
                                    if (calculationMethod === 'individual') {
                                        // Return all individual responses
                                        return personResponses;
                                    } else {
                                        // Calculate per-person average
                                        const totalQuestions = factor.questions.length;
                                        const minRequired = Math.ceil(totalQuestions * (minCompletionThreshold / 100));
                                        if (personResponses.length >= minRequired) {
                                            const average = personResponses.reduce((sum, val) => sum + val, 0) / personResponses.length;
                                            return [average];
                                        }
                                        return [];
                                    }
                                }
                            } else if (selectedQuestion.isOutcome) {
                                // Handle single outcome questions
                                if (!response.employee_outcomes || !response.employee_outcomes.responses) {
                                    return [];
                                }
                                return response.employee_outcomes.responses.filter(r => 
                                    r.q === selectedQuestion.q && r.s === selectedQuestion.s
                                ).map(r => r.response);
                            } else {
                                // Handle single regular questions
                                const matchingQuestions = response.questions?.filter(q => 
                                    q.id === selectedQuestion.dimension && 
                                    q.factor === selectedQuestion.factor && 
                                    q.q === selectedQuestion.q
                                );
                                return matchingQuestions?.map(q => q.response);
                            }
                        })
                        .flat()
                        .filter(val => {
                            // Filter out null, undefined, 'N/A' responses and ensure it's a valid number
                            return val !== null && 
                                   val !== undefined && 
                                   val !== 'N/A' && 
                                   val !== '' &&
                                   !isNaN(Number(val));
                        }).map(val => Number(val)); // Convert to numbers

                    if (categoryResponses.length === 0) return null;
                    
                    // Check minimum response threshold for anchor breakdown
                    if (categoryResponses.length <= minimumResponses) return null;

                    // Count responses in each category for this breakdown using exclusive upper bounds to avoid gaps
                    const categoryLowCount = categoryResponses.filter(score => score >= 0 && score < customRanges.Neutral.min).length;
                    const categoryNeutralCount = categoryResponses.filter(score => score >= customRanges.Neutral.min && score < customRanges.High.min).length;
                    const categoryHighCount = categoryResponses.filter(score => score >= customRanges.High.min && score <= 10).length;
                    
                    // Total valid responses for this category
                    const categoryTotalCategorized = categoryLowCount + categoryNeutralCount + categoryHighCount;
                    
                    if (categoryTotalCategorized === 0) return null;

                    const categoryDistribution = [
                        {
                            name: "Low",
                            data: [(categoryLowCount / categoryTotalCategorized) * 100],
                            color: chartOptions[0].color,
                            count: categoryLowCount
                        },
                        {
                            name: "Neutral",
                            data: [(categoryNeutralCount / categoryTotalCategorized) * 100],
                            color: chartOptions[1].color,
                            count: categoryNeutralCount
                        },
                        {
                            name: "High",
                            data: [(categoryHighCount / categoryTotalCategorized) * 100],
                            color: chartOptions[2].color,
                            count: categoryHighCount
                        }
                    ];

                    return {
                        title: option.name,
                        distribution: categoryDistribution
                    };
                }).filter(Boolean);
                anchorBreakdowns.sort((a, b) => {
                    const aHigh = a.distribution.find(entry => entry.name === "High")?.data[0] || 0;
                    const bHigh = b.distribution.find(entry => entry.name === "High")?.data[0] || 0;
                    return sortDirection === 'desc' ? bHigh - aHigh : aHigh - bHigh;
                });
            }
        }


        // Calculate skip percentage
        const skipPercentage = totalPotentialResponses > 0 ? 
            Math.round((skippedResponses / totalPotentialResponses) * 100) : 0;

        return {
            distributionData: {
                totalResponses: responses.length,
                totalPotentialResponses,
                skippedResponses,
                skipPercentage,
                distribution
            },
            anchorBreakdowns
        };
    }, [data, selectedQuestion, anchor, core_data, isFactorView, customRanges, sortDirection, calculationMethod, minCompletionThreshold]);

    if (!distributionData) {
        return <div>No data available for this question.</div>;
    }

    return (
        <Container>
            <TextContainer>
                <Text typographyStyle={typography.body.regular} color={colors.text.secondary}>
                    {isFactorView ? 'Total Respondents' : 'Total Responses'}: {distributionData.totalResponses}
                </Text>
                {distributionData.skipPercentage > 0 && (
                    <Text typographyStyle={typography.caption.regular} color={colors.text.light} style={{ marginTop: '4px' }}>
                        {distributionData.skipPercentage}% of {isFactorView ? 'respondents' : 'responses'} skipped
                    </Text>
                )}
                {isFactorView && selectedQuestion.questionCount && (
                    <Text typographyStyle={typography.caption.regular} color={colors.text.light} style={{ marginTop: '4px' }}>
                        Aggregated from {selectedQuestion.questionCount} question{selectedQuestion.questionCount !== 1 ? 's' : ''}
                    </Text>
                )}
            </TextContainer>

            <ChartContainer>
                <QuestionDistributionChart data={distributionData.distribution}
                    legend={
                        <Legend>
                            <LegendItem>
                                <ColorBox color="#F15062" />
                                <Text typographyStyle={typography.caption.regular} color={colors.text.secondary}>
                                    Low ({customRanges.Low.min}-{customRanges.Low.max})
                                </Text>
                            </LegendItem>
                            <LegendItem>
                                <ColorBox color="#B3B3B3" />
                                <Text typographyStyle={typography.caption.regular} color={colors.text.secondary}>
                                    Neutral ({customRanges.Neutral.min}-{customRanges.Neutral.max})
                                </Text>
                            </LegendItem>
                            <LegendItem>
                                <ColorBox color="#10B981" />
                                <Text typographyStyle={typography.caption.regular} color={colors.text.secondary}>
                                    High ({customRanges.High.min}-{customRanges.High.max})
                                </Text>
                            </LegendItem>
                        </Legend>
                    }
                />
            </ChartContainer>

            {anchorBreakdowns && anchorBreakdowns.length > 1 && (
                <AnchorBreakdowns>
                    <Text typographyStyle={typography.subhead.semibold} color={colors.text.dark}>
                        Distribution by {core_data?.categories?.[0]?.categories?.find(cat => cat.id === anchor)?.name || 'Category'}
                    </Text>
                    {anchorBreakdowns.map((breakdown, index) => (
                        <MiniQuestionChart
                            key={index}
                            title={breakdown.title}
                            data={breakdown.distribution}
                        />
                    ))}
                </AnchorBreakdowns>
            )}
        </Container>
    );
};

export default QuestionContent;

const Container = styled.div`
    padding: 20px;
`;

const TextContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
        margin-bottom: 32px;
`;

const ChartContainer = styled.div`
    height: 80px;
    margin-bottom: 16px;
    width: 100%;
`;

const AnchorBreakdowns = styled.div`
    margin: 40px 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
    padding-top: 16px;
`;

const Legend = styled.div`
    display: flex;
    gap: 20px;
    align-items: center;
`;

const LegendItem = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const ColorBox = styled.div`
    width: 16px;
    height: 16px;
    background-color: ${props => props.color};
    border-radius: 4px;
`;
